import { useEffect, useRef, useState } from 'react';
import { KEY_RECOIL, EMITTER_CONSTANTS, DEVICE_TAB_TYPES } from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { clearApolloCache } from '@/shared/api';
import {
  fetchAIProcessingStatus,
  selectActiveTab,
  selectIsWaiting,
  useReportStore,
} from '@/entities/report';
import type { WrapReportProps } from './index';

/**
 * Drives the strips management tab shell, exposing the active event sub-tab and ECG data map while keeping the tab in sync with background AI recapture and report rendering. Polls the AI processing status every 60 seconds while a recapture request is pending, and listens for report update and recapture emitter events to clear the Apollo cache and trigger a strip reload.
 *
 * @param props - The wrapper props, whose reportData supplies the report id and study isNonPhi flag matched against incoming report update events.
 * @returns An object carrying activeButton, ecgDataMap, isDeviceEvent and isShowTabHeader.
 */
const useWrapReport = ({ reportData }: Pick<WrapReportProps, 'reportData'>) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;
  const [isGeneratingReport, setGeneratingReport] = useState(false);
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  const isDeviceEvent = DEVICE_TAB_TYPES.includes(activeButton as any);
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);
  const isWaiting = useReportStore(selectIsWaiting(keyRecoil));
  const setWaitingAction = useReportStore((s) => s.actions.setIsWaiting);
  /**
   * Marks whether the strips management tab is still waiting on a background recapture. Forwards the value, or an updater for it, to the report store action bound to the TAB_3 key.
   *
   * @param v - The new waiting flag, or an updater receiving the previous flag.
   * @returns Nothing.
   */
  const setWaiting = (v: Parameters<typeof setWaitingAction>[1]) => setWaitingAction(keyRecoil, v);
  const intervalId = useRef<any>(null);
  const isInitial = useRef(true);
  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo (atom default is {}) - pre-existing gap.
  const { profileId } = useReportStore((s) => s.reportInfo) as any;

  /**
   * Finishes a strip recapture by refreshing the tab with the newly generated strips. Clears the Apollo cache, removes the stored recapture request id from localStorage, drops the waiting flag and emits the reload event strip signal.
   *
   * @returns A promise resolving once the cache is cleared and the reload signal is emitted.
   */
  const handleReloadRecapture = async () => {
    await clearApolloCache();
    localStorage.removeItem(`requestId-recapture-${profileId}`);
    setWaiting(false);
    emitter.emit(EMITTER_CONSTANTS.RELOAD_EVENT_STRIP);
  };

  /**
   * Determines whether a previously started strip recapture is still running for this profile. Reads the stored request id from localStorage, clears the waiting flag when none exists, queries the AI processing status and keeps waiting while commands remain, otherwise reloads the recaptured strips; failures are logged to the console.
   *
   * @returns A promise resolving once the recapture status has been checked and the waiting flag settled.
   */
  const checkProcessingRecapture = async () => {
    try {
      const keyStorage = `requestId-recapture-${profileId}`;
      const requestId = JSON.parse(localStorage.getItem(keyStorage));
      if (!requestId) {
        setWaiting(false);
        return;
      }
      const result: any = await fetchAIProcessingStatus(requestId, 'processingCmd');
      if (result.length > 0) {
        setWaiting((prev) => prev || true);
      } else {
        handleReloadRecapture();
      }
    } catch (error) {
      console.error('Error fetching AI processing status:', error);
    }
  };

  useEffect(() => {
    if (isInitial.current) {
      checkProcessingRecapture();
      isInitial.current = false;
    } else if (isWaiting) {
      intervalId.current = setInterval(() => {
        checkProcessingRecapture();
      }, 60000);
    }
    return () => clearInterval(intervalId.current);
  }, [isWaiting]);

  useEmitter(
    EMITTER_CONSTANTS.REPORT_UPDATED,
    async (msg: any) => {
      if (msg.id === reportData?.report?.id && msg.isNonPhi === reportData?.study?.isNonPhi) {
        if (msg.status === 'Rendering') {
          setGeneratingReport(true);
        }
      }
      if (msg.id === reportData?.report?.id && msg.isNonPhi === reportData?.study?.isNonPhi) {
        if (isGeneratingReport && msg.status === 'Generated') {
          await clearApolloCache();
          emitter.emit(EMITTER_CONSTANTS.RELOAD_EVENT_STRIP);
        }
      }
    },
    [reportData?.report?.id, isGeneratingReport],
  );

  useEmitter(EMITTER_CONSTANTS.RE_CAPTURE_AUTO_EVENTS, handleReloadRecapture, [profileId]);

  return {
    activeButton,
    ecgDataMap,
    isDeviceEvent,
    isShowTabHeader,
  };
};

export default useWrapReport;
